<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ExchangeRateDateRule',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    item: {
      type: Object,
      required: true,
    },
    editing: {
      type: Boolean,
      required: false,
    },
    labelColsMd: {
      type: null,
      required: false,
      default: 3,
    },
    contentColsMd: {
      type: null,
      required: false,
      default: 9,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const offsetDays = ref(null);

  const $refs = {
    get offsetDays() {
      return offsetDays.value;
    },
  };

  const labelAlign = ref('right');
  const metadata = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    labelAlign: labelAlign.value,
    metadata: metadata.value,
    ux: ux.value,
  }));

  const $$load_metadata = () => {
    return $app.value.repo.get('ui_metadata');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'labelAlign' }, { name: 'metadata' }, { name: 'ux' }],
    loaders: { metadata: $$load_metadata },
    ux: {},
    dataRefs: { labelAlign, metadata, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const referenceMonthOptions = computed(() => {
    if (loaded.value) {
      return metadata.value.exchange_rate_date_reference_month_options;
    }
    return null;
  });
  const daySpecifierOptions = computed(() => {
    if (loaded.value) {
      return metadata.value.exchange_rate_date_day_specifier_options;
    }
    return null;
  });
  const target = computed(() => {
    return props.item;
  });

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.editing,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('editing');
    }
  );
  watch(
    () => props.labelColsMd,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelColsMd');
    }
  );
  watch(
    () => props.contentColsMd,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contentColsMd');
    }
  );

  const act_7315092382398562_reloadComponentData_n1f8dc12z =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_6hjbvhd9y =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_n1f8dc12z,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_6hjbvhd9y],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_6hjbvhd9y,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_6hjbvhd9y = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_6hjbvhd9y.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpFormGroup
      :label-cols-md="3"
      :content-cols-md="9"
      name="description"
      :label="pgettext('ExchangeRateDateRule', 'Description')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput name="description" v-model="item.description">
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="3"
      :content-cols-md="9"
      name="reference_month"
      :label="pgettext('ExchangeRateDateRule', 'Reference month')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormNativeSelect
        name="reference_month"
        v-model="item.reference_month"
        :options="referenceMonthOptions"
        valueField="value"
        textField="name"
        required
      >
      </ItpFormNativeSelect>
    </ItpFormGroup>
    <ItpFormGroup
      :labelColsMd="labelColsMd"
      :contentColsMd="contentColsMd"
      name="day_specifier"
      :label="pgettext('ExchangeRateDateRule', 'Day specifier')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormNativeSelect
        name="day_specifier"
        v-model="item.day_specifier"
        :options="daySpecifierOptions"
        valueField="value"
        textField="name"
        required
      >
      </ItpFormNativeSelect>
    </ItpFormGroup>
    <ItpFormGroup
      :labelColsMd="labelColsMd"
      :contentColsMd="contentColsMd"
      name="day_value"
      :label="pgettext('ExchangeRateDateRule', 'Day value')"
      :labelAlign="labelAlign"
      contentClass="input-group"
    >
      <ItpFormInput
        type="number"
        name="day_value"
        v-model="item.day_value"
        ref="offsetDays"
      >
      </ItpFormInput>
      <span class="input-group-text">
        {{ pgettext('InputGroupAddon', 'day') }}
      </span>
    </ItpFormGroup>
  </ItpUsercontrol>
</template>
